import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
    IconDownload,
    IconFeather,
    IconFlame,
    IconRefresh,
    IconScale,
} from '@tabler/icons-react';
import { notifications } from '@/lib/notifications';
import { useTranslation } from '../../../../../i18n/hooks/useTranslation';
import { toBnDigits } from '../../../../../utils/bnFormat';
import { recordToolUsage } from '../../../../../services/toolsService';
import { zipImageFiles } from '../../pdf/pdf-to-image/pdfToImageEngine';
import useImagePaste from '../../components/image/useImagePaste';
import ToolPageLayout from '../../components/common/ToolPageLayout';
import ToolPreviewPanel from '../../components/common/ToolPreviewPanel';
import ImageGridPreview from '../shared/ImageGridPreview';
import ToolCard from '../../components/common/ToolCard';
import Hint from '../../../../../components/common/Hint';
import ImagePreviewModal from '../../../../../components/common/ImagePreviewModal';
import {
    ACCEPT,
    MAX_FILE_BYTES,
    PRESET_IDS,
    compressGifFile,
    isSupportedGifFile,
} from './compressGifImageEngine';
import {
    deriveToolStatus,
    downloadBlob,
    getToolStatusText,
} from '../../toolPageUtils';

// Ordered light → strong so the row reads as one dial; `balanced` is the default.
const PRESET_OPTIONS = [
    { id: 'light', labelKey: 'compressGifImage.presets.light', Icon: IconFeather },
    { id: 'balanced', labelKey: 'compressGifImage.presets.balanced', Icon: IconScale },
    { id: 'strong', labelKey: 'compressGifImage.presets.strong', Icon: IconFlame },
].filter((opt) => PRESET_IDS.includes(opt.id));

function segBtnClass(active) {
    return `flex flex-1 min-h-[54px] flex-col items-center justify-center gap-1 rounded-lg border-none px-1 py-1.5 text-[11px] leading-tight transition-all disabled:cursor-not-allowed disabled:opacity-45 ${
        active
            ? 'bg-white font-semibold text-amber-600 shadow-[0_1px_2px_rgba(28,24,21,0.04)] dark:bg-zinc-900 dark:text-amber-400'
            : 'bg-transparent font-medium text-[#6B635C] dark:text-zinc-400'
    }`;
}

const fileKey = (file) => `${file.name}-${file.size}`;

export default function CompressGifImage() {
    const { t } = useTranslation();
    const fileInputRef = useRef(null);

    const [files, setFiles] = useState([]);
    const [preset, setPreset] = useState('balanced');
    const [isDragging, setIsDragging] = useState(false);
    const [processing, setProcessing] = useState(false);
    const [progress, setProgress] = useState(null);
    const [previewIndex, setPreviewIndex] = useState(0);
    const [results, setResults] = useState([]);
    const [error, setError] = useState(null);

    const invalidateResults = () => {
        setResults([]);
        setError(null);
        setProgress(null);
        setPreviewIndex(0);
    };

    const addFiles = useCallback((incoming) => {
        const list = Array.from(incoming || []);
        const gifs = list.filter(isSupportedGifFile);
        if (!gifs.length) {
            const msg = t('compressGifImage.errors.invalidType');
            setError(msg);
            notifications.show({ title: t('common.error'), message: msg, color: 'red' });
            return;
        }

        let tooLarge = false;
        setFiles((prev) => {
            const next = [...prev];
            for (const file of gifs) {
                if (file.size > MAX_FILE_BYTES) {
                    tooLarge = true;
                    continue;
                }
                if (next.some((f) => fileKey(f) === fileKey(file))) continue;
                next.push(file);
            }
            return next;
        });

        if (tooLarge) {
            const msg = t('compressGifImage.errors.tooLarge');
            setError(msg);
            notifications.show({ title: t('common.error'), message: msg, color: 'red' });
        } else {
            setError(null);
        }
    }, [t]);

    // Ctrl/Cmd+V anywhere on the page drops a copied GIF straight in.
    useImagePaste(addFiles);

    const handleDrop = (e) => {
        e.preventDefault();
        setIsDragging(false);
        addFiles(e.dataTransfer.files);
    };

    const removeFile = (index) => {
        setFiles((prev) => prev.filter((_, i) => i !== index));
        invalidateResults();
    };

    const handleReset = () => {
        setFiles([]);
        invalidateResults();
    };

    // Files with no result yet — adding GIFs to a finished batch queues only the
    // new ones, so nothing already compressed goes through the encoder twice.
    const pendingIndexes = useMemo(
        () => files.reduce((acc, _file, i) => (results[i] ? acc : [...acc, i]), []),
        [files, results],
    );
    const allDone = files.length > 0 && pendingIndexes.length === 0;
    const canRun = pendingIndexes.length > 0;

    const handleCompress = async () => {
        if (!canRun || processing) return;

        recordToolUsage('image/compress-gif', { category: 'image' });

        setProcessing(true);
        setError(null);
        const queue = pendingIndexes;

        try {
            for (let k = 0; k < queue.length; k += 1) {
                const i = queue[k];
                setProgress({ fileIndex: k + 1, fileTotal: queue.length, frame: 0, frames: 0 });

                const result = await compressGifFile(files[i], { preset }, (frameProgress) => {
                    setProgress({
                        fileIndex: k + 1,
                        fileTotal: queue.length,
                        frame: frameProgress.frame,
                        frames: frameProgress.frames,
                    });
                });

                // Written at the file's own index (results line up with files) and
                // published immediately, so its card stops spinning right away.
                setResults((prev) => {
                    const next = [...prev];
                    next[i] = result;
                    return next;
                });
            }

            notifications.show({ title: t('common.success'), message: t('compressGifImage.done'), color: 'green' });
        } catch {
            const msg = t('compressGifImage.errors.failed');
            setError(msg);
            notifications.show({ title: t('common.error'), message: msg, color: 'red' });
        } finally {
            setProcessing(false);
            setProgress(null);
        }
    };

    const doneResults = useMemo(() => results.filter(Boolean), [results]);

    const handleDownload = async () => {
        if (!doneResults.length) return;

        if (doneResults.length === 1) {
            downloadBlob(doneResults[0].blob, doneResults[0].name);
            return;
        }

        const zip = await zipImageFiles(doneResults.map((r) => ({ name: r.name, blob: r.blob })));
        downloadBlob(zip, 'compressed-gifs.zip');
    };

    const status = deriveToolStatus({
        hasInput: files.length > 0,
        canRun,
        processing,
        hasResult: doneResults.length > 0,
        hasError: !!error,
    });

    // ── Before/after lightbox ──
    // Object URLs live as long as the results do; the modal only ever gets URLs,
    // never blobs, so they are made once here instead of per render.
    const [lightboxIndex, setLightboxIndex] = useState(null);
    const [compareUrls, setCompareUrls] = useState([]);

    useEffect(() => {
        if (!doneResults.length) {
            setCompareUrls([]);
            return undefined;
        }
        const made = results.map((r, i) => ({
            after: r ? URL.createObjectURL(r.blob) : null,
            before: files[i] ? URL.createObjectURL(files[i]) : null,
        }));
        setCompareUrls(made);
        return () => made.forEach(({ after, before }) => {
            if (after) URL.revokeObjectURL(after);
            if (before) URL.revokeObjectURL(before);
        });
    }, [results, doneResults.length, files]);

    const compareItems = useMemo(
        () => results
            .map((r, i) => (r ? {
                id: i,
                fileName: r.name,
                mimeType: r.blob.type || 'image/gif',
                aiImage: compareUrls[i]?.after,
                // What the shared modal calls the "before" pane.
                uploadedImage: compareUrls[i]?.before,
            } : null))
            .filter((item) => item?.aiImage),
        [results, compareUrls],
    );

    const openLightbox = (index) => {
        if (!results[index]) return;
        setPreviewIndex(index);
        setLightboxIndex(index);
    };

    const successLabel = doneResults.length > 1
        ? `${t('compressGifImage.resultsTitle')} (${toBnDigits(doneResults.length)})`
        : t('compressGifImage.done');

    // Frames are the honest unit here — one GIF can take a while on its own.
    const processingLabel = progress?.frames > 0
        ? t('compressGifImage.progressFrames', {
            frame: toBnDigits(progress.frame),
            frames: toBnDigits(progress.frames),
        })
        : progress?.fileTotal > 1
            ? t('compressGifImage.progressFileOnly', {
                file: toBnDigits(progress.fileIndex),
                files: toBnDigits(progress.fileTotal),
            })
            : t('compressGifImage.compressing');

    const statusText = getToolStatusText(status, t, {
        ready: t('toolPage.statusText.ready'),
        success: t('compressGifImage.done'),
    });

    return (
        <>
        <ToolPageLayout
            slug="image/compress-gif"
            subtitleKey="compressGifImage.subtitle"
            status={status}
            statusText={allDone && !processing ? successLabel : statusText}
            onReset={files.length || error ? handleReset : undefined}
            // Before a run the bar runs the tool; once every picture is done it downloads.
            primaryAction={allDone && !processing ? {
                label: doneResults.length > 1 ? t('compressGifImage.downloadZip') : t('compressGifImage.download'),
                onClick: handleDownload,
                icon: IconDownload,
            } : {
                label: t('compressGifImage.compress'),
                onClick: handleCompress,
                disabled: !canRun || processing,
                loading: processing,
            }}
            // Drops the results only — the pictures and settings stay loaded, so another
            // run is one click away. Clearing everything is what রিসেট is for.
            secondaryAction={allDone && !processing ? {
                label: t('compressGifImage.startOver'),
                onClick: invalidateResults,
                icon: IconRefresh,
            } : undefined}
            showTrust={false}
            settings={(
                <div className="flex flex-col gap-3">
                    {/* Files are picked in the preview panel, so the left column
                        carries settings only — this input is what both the empty
                        state and the grid's "+" tile open. */}
                    <input
                        ref={fileInputRef}
                        type="file"
                        accept={ACCEPT}
                        multiple
                        className="hidden"
                        onChange={(e) => { addFiles(e.target.files); e.target.value = ''; }}
                    />

                    <ToolCard title={t('compressGifImage.presetTitle')}>
                        <div className="flex gap-1 rounded-xl bg-[#F7F5F3] p-1 dark:bg-zinc-800">
                            {/* Icons only. The tooltip leads with the preset's name —
                                with no label on the button, the description alone
                                would never say which preset is which — and
                                aria-label carries the name for screen readers. */}
                            {PRESET_OPTIONS.map((opt) => (
                                <Hint label={`${t(opt.labelKey)} — ${t(`${opt.labelKey}Desc`)}`} key={opt.id}>
                                    <button
                                        type="button"
                                        onClick={() => { setPreset(opt.id); invalidateResults(); }}
                                        disabled={processing}
                                        className={segBtnClass(preset === opt.id)}
                                        aria-label={t(opt.labelKey)}
                                        aria-pressed={preset === opt.id}
                                    >
                                        <opt.Icon size={20} stroke={1.8} />
                                    </button>
                                </Hint>
                            ))}
                        </div>
                        <p className="mt-2 text-[11.5px] leading-relaxed text-[#A8A099] dark:text-zinc-500">
                            {t(`compressGifImage.presets.${preset}Desc`)}
                        </p>
                    </ToolCard>
                </div>
            )}
            preview={(
                <ToolPreviewPanel
                    // Fixed height suits one or two big pictures; a grid of three or more just hugs its rows.
                    tall={files.length <= 2}
                    title={t('compressGifImage.previewTitle')}
                    status={status}
                    statusLabel={t(`toolPage.status.${status}`)}
                    idleTitle={t('compressGifImage.previewEmpty')}
                    idleHint={t('compressGifImage.previewEmptyHint')}
                    processingLabel={processingLabel}
                    progress={progress?.frames ? Math.round((progress.frame / progress.frames) * 100) : undefined}
                    error={error}
                    canvasTone="plain"
                    processingTone="none"
                    showPasteHint
                    showIdle={!files.length && !error}
                    onIdleClick={() => fileInputRef.current?.click()}
                    onIdleDrop={handleDrop}
                    onIdleDragOver={(e) => { e.preventDefault(); setIsDragging(true); }}
                    onIdleDragLeave={() => setIsDragging(false)}
                    idleDragging={isDragging}
                >
                    {/* Kept mounted while processing: each card spins on its own, so
                        the wait shows the work and finished GIFs can be opened. */}
                    {files.length > 0 && !error ? (
                        <ImageGridPreview
                            // One picture fills the panel, two sit side by side, three or more make a grid.
                            adaptive
                            files={files}
                            results={results}
                            busyIndexes={processing ? pendingIndexes : undefined}
                            activeIndex={doneResults.length ? previewIndex : undefined}
                            onItemClick={doneResults.length ? openLightbox : undefined}
                            onAddMore={processing ? undefined : () => fileInputRef.current?.click()}
                            onAddDrop={processing ? undefined : handleDrop}
                            addLabel={t('compressGifImage.addMore')}
                        />
                    ) : null}
                </ToolPreviewPanel>
            )}
        />
        <ImagePreviewModal
            opened={lightboxIndex !== null}
            onClose={() => setLightboxIndex(null)}
            images={compareItems}
            initialImage={compareItems.find((item) => item.id === lightboxIndex) ?? null}
            onDownloadImage={(img) => {
                const r = results[img?.id];
                if (!r) return;
                downloadBlob(r.blob, r.name);
            }}
            initialViewMode="side-by-side"
            useImageTitle
            hideEdit
            hidePrint
            hideShare
            hideCustomDownload
            hideDelete
        />
        </>
    );
}
